import { Injectable, inject } from '@angular/core';
import { BrowserApiSupportService } from '@lentau/ui-kit/core/services/browser-api-support/browser-api-support.service';
import { IS_SEARCH_BOT } from '@lentau/ui-kit/core/tokens/is-search-bot';
import { SESSION_STORAGE } from '@lentau/ui-kit/core/tokens/session-storage';
import { WINDOW } from '@lentau/ui-kit/core/tokens/window';
import { SentryLogger } from '@lentau/core/sentry/shared/services/sentry-logger.service';
import { ErrorLevel } from '@lentau/core/sentry/shared/services/sentry.level.const';
import { ClientReloadPolicyService } from '../config/client-reload-policy.service';
import {
  RUNTIME_RECOVERY_DEFAULT_MAX_ATTEMPTS,
  RUNTIME_RECOVERY_SECOND_ATTEMPT_DELAY_MS,
  RUNTIME_RECOVERY_DEFAULT_THROTTLE_MS,
  RUNTIME_RECOVERY_FALLBACK_ID,
  RUNTIME_RECOVERY_RECOVERABLE_REASONS,
  RUNTIME_RECOVERY_STATE_KEY,
} from './runtime-recovery.constants';
import { RuntimeRecoveryGlobalApi, RuntimeRecoveryState, RuntimeRecoveryWindow } from './runtime-recovery.interfaces';
import { RuntimeRecoveryReason } from './runtime-recovery.types';
import { shouldSkipRuntimeRecoveryFallback } from '@lentau/core/runtime-recovery/shared/runtime-recovery-skip.utils';
import { loadRuntimeRecoveryFallbackUi } from './runtime-recovery-fallback-ui.helper';

@Injectable({
  providedIn: 'root',
})
export class RuntimeRecoveryOrchestratorService {
  private readonly browserApiSupportService: BrowserApiSupportService = inject(BrowserApiSupportService);
  private readonly sessionStorage: Storage = inject(SESSION_STORAGE);
  private readonly window: RuntimeRecoveryWindow = inject(WINDOW) as RuntimeRecoveryWindow;
  private readonly isSearchBot: boolean = inject(IS_SEARCH_BOT);
  private readonly sentryLogger: SentryLogger = inject(SentryLogger);
  private readonly clientReloadPolicyService: ClientReloadPolicyService = inject(ClientReloadPolicyService);

  public recover(reason: RuntimeRecoveryReason, payload?: unknown): boolean {
    if (this.browserApiSupportService.isServer()) {
      return false;
    }

    if (shouldSkipRuntimeRecoveryFallback(this.window, this.window.document)) {
      return false;
    }

    if (this.isSearchBot) {
      return false;
    }

    if (!RUNTIME_RECOVERY_RECOVERABLE_REASONS.includes(reason)) {
      return false;
    }

    if (!this.clientReloadPolicyService.isChunkAutoReloadEnabled()) {
      return false;
    }

    const globalApi: RuntimeRecoveryGlobalApi | null = this.getGlobalApi();
    if (globalApi !== null) {
      return globalApi.recover(reason, this.buildGlobalPayload(payload));
    }

    return this.recoverWithoutGlobalApi(reason, payload);
  }

  private recoverWithoutGlobalApi(reason: RuntimeRecoveryReason, payload?: unknown): boolean {
    const state: RuntimeRecoveryState = this.getState();
    const maxAttempts: number = this.resolveMaxAttempts();
    const throttleMs: number = this.resolveThrottleMs();
    const now: number = Date.now();
    const isCycleExpired: boolean = state.lastReloadAt > 0 && now - state.lastReloadAt > throttleMs;
    const effectiveState: RuntimeRecoveryState = isCycleExpired ? this.getInitialState() : state;

    if (effectiveState.attempts >= maxAttempts) {
      void this.sentryLogger.handleError('Runtime recovery fallback shown', ErrorLevel.WARNING, {
        source: 'RuntimeRecoveryOrchestratorService.recoverWithoutGlobalApi',
        reason,
        attempts: effectiveState.attempts,
        maxAttempts,
      });
      this.showNativeFallback(reason);
      return false;
    }

    const nextState: RuntimeRecoveryState = {
      attempts: effectiveState.attempts + 1,
      lastReloadAt: now,
      lastReason: reason,
    };

    this.persistState(nextState);
    void this.sentryLogger.handleError('Runtime recovery reload requested', ErrorLevel.INFO, {
      source: 'RuntimeRecoveryOrchestratorService.recoverWithoutGlobalApi',
      reason,
      payload,
      attempts: nextState.attempts,
      maxAttempts,
    });

    this.triggerReloadWithDelayIfNeeded(effectiveState.attempts);
    return true;
  }

  private triggerReloadWithDelayIfNeeded(currentAttempts: number): void {
    const isSecondAttempt: boolean = currentAttempts === 1;
    if (!isSecondAttempt) {
      this.window.location.reload();
      return;
    }

    const secondAttemptDelayMs: number = this.resolveSecondAttemptDelayMs();
    if (secondAttemptDelayMs <= 0) {
      this.window.location.reload();
      return;
    }

    this.window.setTimeout(() => this.window.location.reload(), secondAttemptDelayMs);
  }

  private showNativeFallback(reason: RuntimeRecoveryReason): void {
    const documentRef: Document = this.window.document;
    if (shouldSkipRuntimeRecoveryFallback(this.window, documentRef)) {
      return;
    }

    if (documentRef.getElementById(RUNTIME_RECOVERY_FALLBACK_ID)) {
      return;
    }

    const globalApi: RuntimeRecoveryGlobalApi | null = this.getGlobalApi();
    if (globalApi !== null) {
      globalApi.showFallback(reason);
      return;
    }

    loadRuntimeRecoveryFallbackUi(this.window, documentRef, reason);
  }

  private getGlobalApi(): RuntimeRecoveryGlobalApi | null {
    const rawApi: unknown = this.window.__luRuntimeRecovery;

    if (!rawApi || typeof rawApi !== 'object') {
      return null;
    }

    const recover: unknown = (rawApi as Record<string, unknown>).recover;
    const showFallback: unknown = (rawApi as Record<string, unknown>).showFallback;

    if (typeof recover !== 'function' || typeof showFallback !== 'function') {
      return null;
    }

    return {
      recover: recover as RuntimeRecoveryGlobalApi['recover'],
      showFallback: showFallback as RuntimeRecoveryGlobalApi['showFallback'],
    };
  }

  private getState(): RuntimeRecoveryState {
    const rawState: string | null = this.sessionStorage.getItem(RUNTIME_RECOVERY_STATE_KEY);
    if (!rawState) {
      return this.getInitialState();
    }

    try {
      const parsedState: unknown = JSON.parse(rawState);
      if (!parsedState || typeof parsedState !== 'object') {
        return this.getInitialState();
      }

      const state: Record<string, unknown> = parsedState as Record<string, unknown>;
      const attempts: number = Number(state.attempts);
      const lastReloadAt: number = Number(state.lastReloadAt);
      const lastReason: unknown = state.lastReason;
      const normalizedReason: RuntimeRecoveryReason | null = this.isRuntimeRecoveryReason(lastReason)
        ? lastReason
        : null;

      return {
        attempts: Number.isFinite(attempts) && attempts > 0 ? attempts : 0,
        lastReloadAt: Number.isFinite(lastReloadAt) && lastReloadAt > 0 ? lastReloadAt : 0,
        lastReason: normalizedReason,
      };
    } catch {
      return this.getInitialState();
    }
  }

  private persistState(state: RuntimeRecoveryState): void {
    this.sessionStorage.setItem(RUNTIME_RECOVERY_STATE_KEY, JSON.stringify(state));
  }

  private getInitialState(): RuntimeRecoveryState {
    return {
      attempts: 0,
      lastReloadAt: 0,
      lastReason: null,
    };
  }

  private resolveThrottleMs(): number {
    const policyValue: number = this.clientReloadPolicyService.getChunkReloadThrottleMs();
    return policyValue > 0 ? policyValue : RUNTIME_RECOVERY_DEFAULT_THROTTLE_MS;
  }

  private resolveMaxAttempts(): number {
    const policyValue: number = this.clientReloadPolicyService.getChunkReloadMaxAttemptsPerSession();
    return policyValue > 0 ? policyValue : RUNTIME_RECOVERY_DEFAULT_MAX_ATTEMPTS;
  }

  private resolveSecondAttemptDelayMs(): number {
    return RUNTIME_RECOVERY_SECOND_ATTEMPT_DELAY_MS;
  }

  private isRuntimeRecoveryReason(value: unknown): value is RuntimeRecoveryReason {
    return typeof value === 'string' && RUNTIME_RECOVERY_RECOVERABLE_REASONS.includes(value);
  }

  private buildGlobalPayload(payload: unknown): Record<string, unknown> {
    return {
      payload,
      runtimeRecoveryPolicy: {
        maxAttempts: this.resolveMaxAttempts(),
        throttleMs: this.resolveThrottleMs(),
        secondAttemptDelayMs: this.resolveSecondAttemptDelayMs(),
      },
    };
  }
}
